import type { LabelResolver } from '../../hooks/useLabelResolver';
import { OTHER_PERMISSIONS_CATEGORY } from '../../lib/labelConstants';
import type { PermissionCatalogItem } from '../../api/types/permissions';
import type { TenantRoleListItemLocal } from '../../api/types/TenantLocal';

export const OUT_OF_CATALOG_GROUP = 'Permisos fuera del catálogo';

export interface PermissionMatrixRow {
    key: string;
    name: string;
    description?: string;
    /** Una celda por rol, en el orden de las columnas. */
    granted: boolean[];
}

export interface PermissionMatrixGroup {
    title: string;
    rows: PermissionMatrixRow[];
}

export interface PermissionMatrix {
    groups: PermissionMatrixGroup[];
    /** Permisos distintos de cada rol, en el orden de las columnas. */
    totals: number[];
}

const byName = (a: PermissionMatrixRow, b: PermissionMatrixRow) => a.name.localeCompare(b.name);

function roleKeySets(roles: TenantRoleListItemLocal[]): Set<string>[] {
    return roles.map((r) => new Set<string>(r.permissions ?? []));
}

/** Filas agrupadas por categoría del resolvedor; «Otros permisos» va al final. */
function groupCatalogRows(
    rows: PermissionMatrixRow[],
    labels: LabelResolver,
): PermissionMatrixGroup[] {
    const buckets = new Map<string, PermissionMatrixRow[]>();
    for (const row of rows) {
        const category = labels.category(row.key);
        const bucket = buckets.get(category);
        if (bucket) bucket.push(row);
        else buckets.set(category, [row]);
    }
    const others = buckets.get(OTHER_PERMISSIONS_CATEGORY);
    buckets.delete(OTHER_PERMISSIONS_CATEGORY);
    const groups = [...buckets.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([title, groupRows]) => ({ title, rows: groupRows.sort(byName) }));
    if (others) groups.push({ title: OTHER_PERMISSIONS_CATEGORY, rows: others.sort(byName) });
    return groups;
}

/** Roles × llaves del catálogo; la celda se marca si la llave está en el rol, sin aplicar techo. */
export function buildPermissionMatrix(
    roles: TenantRoleListItemLocal[],
    catalog: PermissionCatalogItem[],
    labels: LabelResolver,
): PermissionMatrix {
    const sets = roleKeySets(roles);
    const toRow = (key: string): PermissionMatrixRow => ({
        key,
        ...labels.permission(key),
        granted: sets.map((s) => s.has(key)),
    });

    const catalogKeys = new Set(catalog.map((c) => c.key));
    const outOfCatalog = new Set(sets.flatMap((s) => [...s]).filter((k) => !catalogKeys.has(k)));

    const groups = groupCatalogRows([...catalogKeys].map(toRow), labels);
    if (outOfCatalog.size > 0) {
        groups.push({
            title: OUT_OF_CATALOG_GROUP,
            rows: [...outOfCatalog].map(toRow).sort(byName),
        });
    }
    return { groups, totals: sets.map((s) => s.size) };
}

function normalize(text: string): string {
    return text
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase('es-MX');
}

/** Filtra filas por nombre o descripción; descarta los grupos vacíos. */
export function filterPermissionMatrix(
    groups: PermissionMatrixGroup[],
    query: string,
): PermissionMatrixGroup[] {
    const needle = normalize(query.trim());
    if (!needle) return groups;
    return groups
        .map((g) => ({
            title: g.title,
            rows: g.rows.filter((r) =>
                normalize(`${r.name} ${r.description ?? ''}`).includes(needle),
            ),
        }))
        .filter((g) => g.rows.length > 0);
}
